import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export const fmtMoney = (n, withSign = false) => {
  const num = Number(n || 0);
  const s = num.toLocaleString("en-US", { style: "currency", currency: "USD" });
  if (withSign && num > 0) return `+${s}`;
  return s;
};

// Asset value formatter: drops trailing ".00" but keeps real cents (e.g. ".50")
export const fmtAsset = (n) => {
  const num = Number(n || 0);
  const whole = Number.isInteger(num);
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

// Annual entity renewal status. Flags within 30 days before the next recurrence.
// An LLC with NO renewal date is also flagged — a missing date means it isn't being tracked.
export const entityRenewal = (renewalDate, type) => {
  if (!renewalDate) {
    const missing = type === "llc";
    return { has: false, due: missing, missing };
  }
  const parts = String(renewalDate).slice(0, 10).split("-").map(Number);
  if (parts.length < 3 || !parts[0]) return { has: false, due: false };
  const [, m, d] = parts;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let next = new Date(today.getFullYear(), m - 1, d);
  next.setHours(0, 0, 0, 0);
  if (next < today) next = new Date(today.getFullYear() + 1, m - 1, d);
  const days = Math.round((next - today) / 86400000);
  return { has: true, next, days, due: days <= 30 };
};

// Real-estate property tax delinquency: any tax_records row marked delinquent.
export const assetDelinquent = (asset) =>
  asset?.asset_type === "real_estate" &&
  Array.isArray(asset.tax_records) &&
  asset.tax_records.some((t) => t.status === "delinquent");

// Best-guess county tax assessor search link from an address.
export const assessorGuess = (address) => {
  const a = (address || "").trim();
  if (!a) return "";
  return `https://www.google.com/search?q=${encodeURIComponent("county property tax assessor parcel search " + a)}`;
};

export const CATEGORY_LABELS = {
  rent: "Rent (income)",
  late_fee_income: "Late Fee (income)",
  other_income: "Other Income",
  property_taxes: "Property Taxes",
  utilities: "Utilities",
  repairs: "Repairs & Maintenance",
  maintenance: "Maintenance",
  property_manager_fees: "Property Manager Fees",
  insurance: "Insurance",
  mortgage: "Mortgage",
  hoa: "HOA",
  owner_draw: "Owner Draw / Disbursement",
  other: "Other",
};

export const INCOME_CATEGORIES = new Set(["rent", "late_fee_income", "other_income"]);

export const CATEGORIES = Object.keys(CATEGORY_LABELS);

export const PROPERTY_TYPES = {
  single_family: "Single Family",
  duplex: "Duplex",
  triplex: "Triplex",
  fourplex: "Fourplex",
  fiveplex: "Five-plex",
  sixplex: "Six-plex",
};

export const ASSET_TYPE_LABELS = {
  real_estate: "Real Estate",
  mineral_royalty: "Oil & Gas",
  private_equity_lp: "Private Funds",
  brokerage_account: "Brokerage Account",
  bank_account: "Bank Account",
  note_receivable: "Mortgage Notes",
  business_interest: "Business Interest",
  precious_metals: "Precious Metals",
  other: "Other",
};

// Derive a Ploy-style status pill from existing fields. No new schema needed.
//   Performing  → has income, no flags
//   Attention   → tax delinquent OR lease expiring
//   Late        → liability due_date past
//   No income   → real_estate with no monthly rent
//   Held        → non-income asset class kept for appreciation
export const holdingStatus = (asset, opts = {}) => {
  const { hasIncome = false, isLate = false, hasAlert = false } = opts;
  if (isLate) return { key: "late", label: "Late", tone: "destructive" };
  if (hasAlert || assetDelinquent(asset)) {
    return { key: "attention", label: "Attention", tone: "warning" };
  }
  if (hasIncome) return { key: "performing", label: "Performing", tone: "success" };
  if (asset?.asset_type === "real_estate") return { key: "no_income", label: "No income", tone: "muted" };
  return { key: "held", label: "Held", tone: "muted" };
};

export const ENTITY_TYPE_LABELS = { llc: "LLC", trust: "Trust", individual: "Individual" };

export const LIABILITY_TYPE_LABELS = {
  mortgage_traditional: "Traditional Mortgage",
  mortgage_dscr_io: "DSCR Interest-Only",
  heloc: "HELOC",
  loan: "Loan",
  line_of_credit: "Line of Credit",
  other: "Other",
};

// Critical-date helper: returns { has, date, days, label } for the nearest lifecycle date.
export const liabilityCriticalDate = (l) => {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const candidates = [
    { field: "maturity_date", label: "Maturity" },
    { field: "io_period_end_date", label: "IO balloon" },
    { field: "draw_period_end_date", label: "Draw period ends" },
  ];
  let nearest = null;
  for (const c of candidates) {
    const raw = l[c.field];
    if (!raw) continue;
    const d = new Date(raw); d.setHours(0, 0, 0, 0);
    const days = Math.round((d - now) / 86400000);
    if (!nearest || days < nearest.days) {
      nearest = { has: true, date: raw, days, label: c.label, dateObj: d };
    }
  }
  return nearest || { has: false };
};

// Liability is "due soon" when its next due_date is within 30 days (or already past).
export const liabilityDue = (due) => {
  if (!due) return { has: false, due: false };
  const parts = String(due).slice(0, 10).split("-").map(Number);
  if (parts.length < 3 || !parts[0]) return { has: false, due: false };
  const [y, m, d] = parts;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const next = new Date(y, m - 1, d); next.setHours(0, 0, 0, 0);
  const days = Math.round((next - today) / 86400000);
  return { has: true, next, days, due: days <= 30 };
};

export const ROLE_LABELS = {
  operator: "Operator", survivor: "Survivor / Spouse", executor: "Executor / Heir",
  cpa: "CPA", attorney: "Attorney",
};

export const PAYOR_KIND_LABELS = {
  property_manager: "Property Manager", mineral_operator: "Mineral Operator",
  fund_admin: "Fund Administrator", tenant: "Tenant", other: "Other",
};

export const ADVISOR_ROLE_LABELS = {
  cpa: "CPA / Accountant", attorney: "Estate Attorney", insurance_agent: "Insurance Agent",
  financial: "Financial Advisor", other: "Other",
};

export const OWNER_LABELS = { A: "Operator A", B: "Operator B", joint: "Joint" };
export const CONFIDENCE_OPTS = ["high", "medium", "low"];
export const REVIEW_OPTS = ["confirmed", "needs_review", "unverified"];

// Categorical color per asset class (HSL triplet from CSS vars). Use inline styles.
export const assetColor = (type) => `hsl(var(--asset-${type || "other"}))`;
export const assetTint = (type, alpha = 0.13) => `hsl(var(--asset-${type || "other"}) / ${alpha})`;
